/* global define */

define([
  'es6!sumo/util',
  'immutable',
  'es6!launcher/actions/ShortcutsActions',
  'es6!launcher/util/shortcuts/utils',
], (Util, Immutable, Actions, ShortcutUtil) => {
  const { formatGlobal, updateNodeVisibility, updateShortcutColor } = ShortcutUtil;
  const { formatMoney } = Util;

  const initialState = Immutable.Map({
    templates: Immutable.List(),
    templatesById: Immutable.Map(),
    loaded: Immutable.Map({
      shortcut: false,
      shortcuts: false,
      settings: false,
    }),
    saving: false,
    activeEmailService: {
      id: 'sumo',
      name: 'Sumo',
      connected: true,
      image: 'sumomail.png',
      disabled: false,
    },
    shortcutTemplate: Immutable.Map(),
    shortcuts: null,
    shortcut: null,
    filteredShortcut: Immutable.Map(),
    currentDisplayRuleUrl: '',
    selectedProductId: '96ff6357-e139-4fda-9801-7136e6cc0a3d', // ecomm monthly
    hasUpgraded: false,
    showUpsell: false,
    hasIntegrated: false,
    segmentationCounts: Immutable.Map(),
    fetchingSegmentCount: false,
    emailTemplateHtml: '',
    tempEmailTemplateHtml: '',
    emailTemplateStyle: Immutable.Map(),
    currency: Immutable.Map(),
    creatingShortcut: false,
    dirty: false,
  });

  const actionHandlers = {};

  actionHandlers[Actions.UPDATE_SHORTCUT_GLOBALS] = (state) => {
    const shortcut = state.get('shortcut');
    if (!shortcut) return state;

    let globals = shortcut.getIn(['definition', 'globals']);
    if (!globals) return state;

    globals.keySeq().forEach((key) => {
      const disabled = globals.getIn([key, 'disabled'], false);
      const format = globals.getIn([key, 'format']);
      const output = disabled ? '' : formatGlobal(shortcut, format, {
        currency: state.get('currency'),
      });

      globals = globals.setIn([key, 'value'], output);
    });

    return state.setIn(['shortcut', 'definition', 'globals'], globals);
  };

  actionHandlers[Actions.VALIDATE_SHORTCUT] = (state) => {
    if (!state.get('shortcut')) return state;
    return actionHandlers[Actions.UPDATE_SHORTCUT_GLOBALS](state);
  };

  actionHandlers[Actions.UPDATE_SHORTCUT] = (state, action) => {
    const { id, attributes } = action;
    let shortcuts = state.get('shortcuts').map((shortcut) => {
      if (id === shortcut.get('id')) {
        let updatedShortcut = shortcut;

        Object.keys(attributes).forEach((key) => {
          updatedShortcut = updatedShortcut.set(key, attributes[key]);
        });

        return updatedShortcut;
      }

      return shortcut;
    });

    shortcuts = shortcuts.filter(shortcut => shortcut.get('status') !== 'deleted');

    return state.set('shortcuts', shortcuts);
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_SETTING] = (state, action) => {
    const { key, value } = action;
    if (Array.isArray(key)) return state.setIn(key, value);
    return state.set(key, value);
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_SETTINGS] = (state, action) => {
    const { settings } = action;
    return state.merge(Immutable.fromJS(settings));
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_DEFINITION] = (state, action) => {
    const { key, value } = action;
    if (Array.isArray(key)) return state.setIn(['shortcut', 'definition', ...key], value);
    return state.setIn(['shortcut', 'definition', key], value);
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_EMAIL_CAMPAIGN] = (state, action) => {
    const { campaign, key, value } = action;
    if (Array.isArray(key)) return state.setIn(['shortcut', 'email_campaigns', campaign, ...key], value);
    return state.setIn(['shortcut', 'email_campaigns', campaign, key], value);
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_FORM] = (state, action) => {
    const { formTemplateId, setting, value } = action;
    if (Array.isArray(setting)) return state.setIn(['shortcut', 'forms', formTemplateId, ...setting], value);
    return state.setIn(['shortcut', 'forms', formTemplateId, setting], value);
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_DISCOUNT] = (state, action) => {
    const { index, setting } = action;
    let { value } = action;

    if (['min_purchase_amount', 'discount_value'].indexOf(setting) > -1) {
      value = parseFloat(value);
      value = parseFloat(value.toFixed(2));
    }

    let newState = state;
    let discount = state.getIn(['shortcut', 'definition', 'discounts', index]);

    if (!discount) return newState;

    let discountValue = discount.get('discount_value');
    let discountType = discount.get('discount_type');

    const overridePrefix = (() => {
      const discountPrefix = discount.get('discount_prefix');
      if (setting === 'discount_prefix') return false;

      if (
        (discountType === 'shipping' && discountPrefix !== 'SHIP')
        || (discountType !== 'shipping' && discountPrefix !== `${discountValue}OFF`)
      ) {
        return false;
      }

      return true;
    })();

    if (setting === 'discount_type') discountType = value;
    else if (setting === 'discount_value') discountValue = value;

    discount = discount.set(setting, value);

    // TODO: Phase out this old discount label stuff
    const discountLabelFormat = newState.getIn(
      ['shortcut', 'definition', 'meta', 'discountLabelFormat'],
      'GET {discount_value} THIS ORDER NOW!',
    );

    const symbol = state.getIn(['currency', 'symbol'], '$');
    const isPrefix = state.getIn('currency', 'isPrefix', true);

    const mergeTags = {
      discount_min_purchase_amount: formatMoney(discount.get('min_purchase_amount', 0), symbol, isPrefix),
    };

    // Generate a prefix and label
    let discountPrefix;
    if (discountType === 'shipping') {
      discountPrefix = 'SHIP';
      mergeTags.discount_value = 'FREE SHIPPING ON';
    } else {
      discountPrefix = `${discountValue}OFF`;

      let discountLabel = discountValue;
      if (discountType === 'percentage') {
        discountLabel += '%';
      } else {
        discountLabel = formatMoney(discountLabel, symbol, isPrefix);
      }

      mergeTags.discount_value = `${discountLabel} OFF`;
    }

    let discountLabel = discountLabelFormat;
    Object.keys(mergeTags).forEach((tag) => {
      discountLabel = discountLabel.replace(new RegExp(`{${tag}}`, 'g'), mergeTags[tag]);
    });

    if (overridePrefix) {
      discount = discount.merge({ discount_prefix: discountPrefix });
    }

    // TODO: Figure out a better way to handle discount labels
    //  since we can have multiple discounts now
    if (index === 0) {
      newState = newState.setIn(
        ['shortcut', 'definition', 'meta', 'discountLabel'],
        discountLabel,
      );

      if (discountType === 'shipping') {
        // Remove all other tiers because they aren't supported
        newState = newState.setIn(['shortcut', 'definition', 'discounts'], Immutable.fromJS([discount]));
      }

      // Make sure all the discounts have the same type
      newState = newState.setIn(
        ['shortcut', 'definition', 'discounts'],
        newState.getIn(['shortcut', 'definition', 'discounts']).map(d => d.set('discount_type', discountType)),
      );

      if (setting === 'min_purchase_amount') {
        // Update in cart value on any forms relying on this campaign
        const forms = newState.getIn(['shortcut', 'forms'], Immutable.Map());
        forms.keySeq().forEach((formName) => {
          const form = forms.get(formName);
          form.get('steps').forEach((stepName) => {
            const step = form.get(stepName);
            if (step.get('discount_campaign_id') === discount.get('id')) {
              newState = newState.setIn(['shortcut', 'forms', formName, stepName, 'min_cart_value'], value);
            }
          });
        });

        // Find min-cart-value steps so we can update the correct forms
        if (value) {
          const sections = newState.getIn(['shortcut', 'definition', 'sections']);
          const steps = sections.reduce((m, s) => m.concat(s.get('steps')), Immutable.List());

          steps.forEach((step) => {
            if (step.get('controls', Immutable.List()).contains('min-cart-value')) {
              const formTemplateId = step.get('formTemplateId');
              const formStep = step.get('formStep');
              const minCartValue = value ? value / 2 : 0.01;
              newState = newState.setIn(['shortcut', 'forms', formTemplateId, formStep, 'min_cart_value'], minCartValue);
            }
          });
        }
      }
    }

    return newState.setIn(['shortcut', 'definition', 'discounts', index], discount);
  };

  actionHandlers[Actions.RECEIVE_SHORTCUT_TEMPLATES] = (state, action) => (
    state.merge(Immutable.Map({
      templates: Immutable.fromJS(action.data.templates),
    }))
  );

  actionHandlers[Actions.RECEIVE_SHORTCUT] = (state, action) => (
    state.set('shortcut', Immutable.fromJS(action.shortcut))
  );

  actionHandlers[Actions.RECEIVE_SHORTCUTS] = (state, action) => (
    state.set('shortcuts', Immutable.fromJS(action.shortcuts))
  );

  actionHandlers[Actions.RECEIVE_SHORTCUT_TEMPLATE] = (state, action) => {
    const { data: { template } } = action;
    return state.setIn(['templatesById', template.id], Immutable.fromJS(template));
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_FORM_COMPONENT] = (state, action) => {
    const {
      step,
      id,
      key,
      value,
    } = action;

    const formTemplateId = step.get('formTemplateId');
    const formStep = step.get('formStep');

    const form = state.getIn([
      'shortcut',
      'forms',
      formTemplateId,
    ]);

    const updatedForm = form.setIn([
      formStep,
      'components',
    ], form.getIn([
      formStep,
      'components',
    ]).map((component) => {
      if (component.get('id') === id) {
        return component[Array.isArray(key) ? 'setIn' : 'set'](key, value);
      }

      return component;
    }));

    return state.setIn(['shortcut', 'forms', formTemplateId], updatedForm);
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_EMAIL_FIELD] = (state, action) => {
    const { emailTemplateId, key, value } = action;

    if (emailTemplateId) {
      const email = state.getIn(['shortcut', 'emails', emailTemplateId]);
      const updatedEmail = email.setIn([].concat(key), value);
      return state.setIn(['shortcut', 'emails', emailTemplateId], updatedEmail);
    }

    // if no emailTemplateId passed, then update all emails
    let newState = state;
    const emails = state.getIn(['shortcut', 'emails']);
    emails.forEach((emailObj, templateId) => {
      const email = state.getIn(['shortcut', 'emails', templateId]);
      const updatedEmail = email.setIn([].concat(key), value);
      newState = newState.setIn(['shortcut', 'emails', templateId], updatedEmail);
    });

    return newState;
  };

  actionHandlers[Actions.UPDATE_SHORTCUT_EMAIL_VARIABLE] = (state, action) => {
    const {
      emailTemplateId,
      id,
      key,
      value,
    } = action;

    const email = state.getIn([
      'shortcut',
      'emails',
      emailTemplateId,
    ]);

    const variables = email.get('variables');
    const updatedVariables = variables.map((variable) => {
      if (variable.get('slug') === id) return variable.setIn([key], value);
      return variable;
    });

    const updatedEmail = email.set('variables', updatedVariables);

    return state.setIn(['shortcut', 'emails', emailTemplateId], updatedEmail);
  };

  actionHandlers[Actions.UPDATE_EMAIL_NODES] = (state, action) => {
    const { emailTemplateId, id, value } = action;

    let email = state.getIn([
      'shortcut',
      'emails',
      emailTemplateId,
    ]);

    const nodeList = email.getIn(['body', 'document', 'nodes'], Immutable.List());
    const nodes = updateNodeVisibility(nodeList, id, value);

    email = email.setIn(['body', 'document', 'nodes'], nodes);
    return state.setIn(['shortcut', 'emails', emailTemplateId], email);
  };

  actionHandlers[Actions.ADD_DISPLAY_RULE] = (state, action) => {
    const { formKey, displayRule } = action;

    // TODO: Add logic to check if multiple forms under each form type
    // (currently only 1 form per type)
    const displayRules = state.getIn(['shortcut', 'forms', formKey, 'displayRules']);
    return state.mergeIn(['shortcut', 'forms', formKey, 'displayRules'],
      displayRules.push(Immutable.fromJS(displayRule)));
  };

  actionHandlers[Actions.REMOVE_DISPLAY_RULE] = (state, action) => {
    const { formKey, displayRule } = action;

    // TODO: Add logic to check if multiple forms under each form type
    // (currently only 1 form per type)
    const displayRules = state.getIn(['shortcut', 'forms', formKey, 'displayRules']);
    const filteredRules = displayRules.filter(val => val.get('filter_value') !== displayRule);

    return state.setIn(['shortcut', 'forms', formKey, 'displayRules'], filteredRules);
  };

  actionHandlers[Actions.LOAD_DISPLAY_RULES] = (state, action) => {
    const { rules, formKey } = action;
    return state.setIn(['shortcut', 'forms', formKey, 'displayRules'], Immutable.fromJS(rules));
  };

  // Receive fetched settings from main ShortcutsContainer mount
  actionHandlers[Actions.RECEIVE_SHORTCUT_SETTINGS] = (state, action) => {
    const { data } = action;
    return state.setIn(['pricingData'], Immutable.fromJS(data.pricingData));
  };

  actionHandlers[Actions.SET_SELECTED_PRODUCT] = (state, { productId }) => (
    state.set('selectedProductId', productId)
  );

  actionHandlers[Actions.UPDATE_SHORTCUT_COLOR] = (state, { key, color }) => (
    state.set('shortcut', updateShortcutColor(state.get('shortcut'), { key, color }))
  );

  actionHandlers[Actions.RECEIVE_EMAIL_TEMPLATE] = (state, action) => {
    const update = {};
    if (action.data.template) update.emailTemplateHtml = Immutable.fromJS(action.data.template)
    if (action.data.tempTemplate) update.tempEmailTemplateHtml = Immutable.fromJS(action.data.tempTemplate)
    return state.merge(Immutable.Map(update));
  };

  actionHandlers[Actions.RECEIVE_EMAIL_MERGE_DATA] = (state, action) => (
    state.setIn(['shortcut', 'emailMergeData'], Immutable.fromJS(action.emailMergeData))
  );

  // Hijack discount settings to get currency information
  actionHandlers[Actions.RECEIVE_DISCOUNT_SETTINGS] = (state, action) => {
    const { settings } = action;
    return state.set('currency', Immutable.fromJS(settings.currency));
  };

  let tempShortcut;
  return Util.createReducer(initialState, actionHandlers, {
    beforeUpdate(state) {
      tempShortcut = state.get('shortcut');
      return state;
    },
    afterUpdate(state) {
      if (tempShortcut !== state.get('shortcut')) {
        return state.set('dirty', true);
      }

      return state;
    },
  });
});
